import { ReactNode } from 'react'

import { Icon } from '~/src/components/generic/PhosphorIcons'

import { toLocalISODateString } from '~/helpers/date'
import { getWeekDateRange } from '~/src/components/Operational/LoadBoardDashboard/helpers'

import i18n from '~/lib/i18n'
import colors from '~/config/colorsScheme'

import {
  BackhaulEligibleCode,
  LegStatus,
  LoadStatus,
  SortInput,
  TractorStatus,
  TrailerType,
} from '~/__generated_types__/globalTypes'

import { Option } from '~/src/components/Operational/LoadBoardDashboard/DetailView/components/EmptyMileSelectDropdown/types'
import { StopStatus } from '~/src/components/Operational/LoadBoardDashboard/types'

export const ORDER_STATUSES: Array<LoadStatus> = [
  LoadStatus.Available,
  LoadStatus.Planned,
  LoadStatus.Dispatched,
  LoadStatus.Started,
  LoadStatus.Suspended,
  LoadStatus.Imported,
  LoadStatus.Completed,
  LoadStatus.Cancelled,
  LoadStatus.InAudit,
]

export const ASSIGNABLE_LEG_STATUS = [
  LegStatus.Planned,
  LegStatus.Available,
  LegStatus.Dispatched,
]

export const SECONDARY_ASSIGNABLE_LOAD_STATUS = [
  LoadStatus.Planned,
  LoadStatus.Available,
  LoadStatus.Dispatched,
  LoadStatus.Started,
  LoadStatus.InAudit,
]

export const SECONDARY_ASSIGNABLE_LEG_STATUS = [
  LegStatus.Planned,
  LegStatus.Available,
  LegStatus.Dispatched,
  LegStatus.Started,
  LegStatus.Completed,
]

export const StopStatusMap: Record<StopStatus, string> = {
  [StopStatus.Completed]: i18n.t(`stops.statuses.${StopStatus.Completed}`, {
    ns: 'operational',
  }),
  [StopStatus.AtLocation]: i18n.t(`stops.statuses.${StopStatus.AtLocation}`, {
    ns: 'operational',
  }),
  [StopStatus.InTransit]: i18n.t(`stops.statuses.${StopStatus.InTransit}`, {
    ns: 'operational',
  }),
  [StopStatus.Planned]: i18n.t(`stops.statuses.${StopStatus.Planned}`, {
    ns: 'operational',
  }),
  [StopStatus.Cancelled]: i18n.t(`stops.statuses.${StopStatus.Cancelled}`, {
    ns: 'operational',
  }),
  [StopStatus.Skipped]: i18n.t(`stops.statuses.${StopStatus.Skipped}`, {
    ns: 'operational',
  }),
  [StopStatus.Attempted]: i18n.t(`stops.statuses.${StopStatus.Attempted}`, {
    ns: 'operational',
  }),
}

export const LegStatusMap: Record<LegStatus, string> = {
  [LegStatus.Available]: i18n.t(`legs.status.${LegStatus.Available}`, {
    ns: 'operational',
  }),
  [LegStatus.Planned]: i18n.t(`legs.status.${LegStatus.Planned}`, {
    ns: 'operational',
  }),
  [LegStatus.Dispatched]: i18n.t(`legs.status.${LegStatus.Dispatched}`, {
    ns: 'operational',
  }),
  [LegStatus.Started]: i18n.t(`legs.status.${LegStatus.Started}`, {
    ns: 'operational',
  }),
  [LegStatus.Completed]: i18n.t(`legs.status.${LegStatus.Completed}`, {
    ns: 'operational',
  }),
  [LegStatus.Cancelled]: i18n.t(`legs.status.${LegStatus.Cancelled}`, {
    ns: 'operational',
  }),
  [LegStatus.Unknown]: i18n.t(`legs.status.${LegStatus.Unknown}`, {
    ns: 'operational',
  }),
}

export const LIMIT = 25

const weekDateRange = getWeekDateRange({ startOffset: 7, endOffset: 1 })

export const EMPTY_FILTER = {
  costCenterIdIn: null,
  driverIdIn: null,
  routeIdIn: null,
  locationIdIn: null,
  ryderNumberEq: null,
  referenceValueEq: null,
  limit: LIMIT,
  offset: 0,
  statusIn: [...ORDER_STATUSES],
  plannedStartDateRange: [
    toLocalISODateString(new Date(weekDateRange.start)),
    toLocalISODateString(new Date(weekDateRange.end)),
  ],
  sortByPlannedStartTime: SortInput.Asc,
}

export const STOP_STATUS_COLOR_MAP: Map<StopStatus, string> = new Map([
  [StopStatus.Completed, 'bg-blue-50 text-blue-800'],
  [StopStatus.AtLocation, 'bg-green-50 text-green-800'],
  [StopStatus.InTransit, 'bg-green-50 text-green-800'],
  [StopStatus.Planned, 'bg-yellow-50 text-yellow-900'],
  [StopStatus.Cancelled, 'bg-gray-100 text-default'],
  [StopStatus.Skipped, 'bg-gray-100 text-default'],
  [StopStatus.Attempted, 'bg-blue-50 text-blue-800'],
])

export const ERROR_DELAY = 15000
export const SUCCESS_DELAY = 5000

export const DETAIL_TABLE_HEADER: Record<
  string,
  { name: ReactNode; widthPct: number; className?: string }
> = {
  idx: { name: '', widthPct: 2, className: 'text-center' },
  location: { name: 'Location', widthPct: 16 },
  stopReferences: { name: 'Stop refs', widthPct: 8 },
  criticalApp: { name: 'Critical', widthPct: 5 },
  app: { name: 'Appointment', widthPct: 15 },
  inout: {
    name: 'In/Out',
    widthPct: 15,
  },
  miles: { name: 'Miles', widthPct: 6 },
  transit: {
    name: (
      <div className="flex flex-col space-y-0.5">
        <span>Transit/</span>
        <span>Dwell</span>
      </div>
    ),
    widthPct: 7,
  },
  stopEvent: { name: 'Event', widthPct: 7 },
  type: { name: 'Stop Type', widthPct: 7 },
  mileageType: { name: 'Mile Type', widthPct: 7 },
  odometer: { name: 'ODO', widthPct: 7 },
  status: { name: 'Status', widthPct: 5 },
  menu: { name: '', widthPct: 4 },
}

export const DETAIL_TABLE_HEADER_V2: Record<
  string,
  { name: ReactNode; widthPct: number; className?: string }
> = {
  idx: { name: '', widthPct: 2, className: 'text-center' },
  location: { name: 'Location', widthPct: 16 },
  app: { name: 'Appointment', widthPct: 16 },
  inout: {
    name: 'In/Out',
    widthPct: 16,
  },
  miles: { name: 'Miles', widthPct: 6 },
  transit: {
    name: (
      <div className="flex flex-col space-y-0.5">
        <span>Transit/</span>
        <span>Dwell</span>
      </div>
    ),
    widthPct: 7,
  },
  stopEvent: { name: 'Event', widthPct: 7 },
  type: { name: 'Stop Type', widthPct: 7 },
  mileageType: { name: 'Mile Type', widthPct: 7 },
  odometer: { name: 'ODO', widthPct: 7 },
  status: { name: 'Status', widthPct: 5 },
  menu: { name: '', widthPct: 4 },
}

const InvalidTMWVersionIDScos = 'InvalidTMWVersionIDScos'
const InvalidTMWVersionIDClient = 'InvalidTMWVersionIDClient'

export const TMW_VERSION_ID_ERRORS = [
  InvalidTMWVersionIDScos,
  InvalidTMWVersionIDClient,
]

// Not editable statuses for empty mile eligibility
export const NON_EDITABLE_LOAD_STATUSES = new Set([
  LoadStatus.Suspended,
  LoadStatus.Imported,
  LoadStatus.Completed,
  LoadStatus.Cancelled,
])

// List of statuses that are editable
export const EDITABLE_LOAD_STATUSES: Array<LoadStatus> = [
  LoadStatus.Available,
  LoadStatus.Planned,
  LoadStatus.Dispatched,
  LoadStatus.Started,
  LoadStatus.InAudit,
]

// This need to be fixed, constants file should not have any JSX
export const EMPTY_MILE_ELIGIBILITY_OPTIONS: Array<Option> = [
  {
    value: i18n.t('emptyMile.eligibleBackhaulValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.eligibleBackhaulDesc', { ns: 'operational' }),
    icon: <Icon color={colors.green[600]} name="CheckIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Yes,
  },
  {
    value: i18n.t('emptyMile.eligibleHeadhaulValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.eligibleHeadhaulDesc', { ns: 'operational' }),
    icon: <Icon color={colors.green[600]} name="CheckIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Yeshh,
  },
  {
    value: i18n.t('emptyMile.eligiblePrebookedValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.eligiblePrebookedDesc', { ns: 'operational' }),
    icon: <Icon color={colors.green[600]} name="CheckIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Yespb,
  },
  {
    value: i18n.t('emptyMile.notDunnageValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notDunnageDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Drturn,
  },
  {
    value: i18n.t('emptyMile.notInternalBackhaulValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notInternalBackhaulDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Intbhl,
  },
  {
    value: i18n.t('emptyMile.notShortDistanceValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notShortDistanceDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Shdist,
  },
  {
    value: i18n.t('emptyMile.notHosConflictValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notHosConflictDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Hoscft,
  },
  {
    value: i18n.t('emptyMile.notDriverNeededValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notDriverNeededDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Dnnasm,
  },
  {
    value: i18n.t('emptyMile.notCustomerPrefValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notCustomerPrefDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Cupref,
  },
  {
    value: i18n.t('emptyMile.notManagerValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notManagerDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Fmdirc,
  },
  {
    value: i18n.t('emptyMile.notWeatherValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notWeatherDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Wethr,
  },
  {
    value: i18n.t('emptyMile.notEquipmentValue', { ns: 'operational' }),
    description: i18n.t('emptyMile.notEquipmentDesc', { ns: 'operational' }),
    icon: <Icon color={colors.red[600]} name="ProhibitIcon" size="16" weight="bold" />,
    code: BackhaulEligibleCode.Spceqp,
  },
]

// TODO remove duplicates once data pipeline has normalized anton db
export const TRACTOR_TYPE: Map<string, string> = new Map([
  ['TA Tractor', 'TA-TRACT'],
  ['T/A Tractor', 'TA-TRACT'],
  ['TA Conv Sleeper Trct', 'TA-SLEEP'],
  ['T/A Conv Sleeper Tractor', 'TA-SLEEP'],
  ['SA Tractor', 'SA-TRACT'],
  ['S/A Tractor', 'SA-TRACT'],
  ['UNKNOWN', 'UNKNOWN'],
])

export const TRACTOR_TYPE_TO_COLOR: Map<string, string> = new Map([
  ['TA-TRACT', 'bg-green-50 text-green-800'],
  ['TA-SLEEP', 'bg-darkPurple-50 text-darkPurple-800'],
  ['SA-TRACT', 'bg-green-50 text-green-800'],
  ['UNKNOWN', 'bg-red-50 text-red-800'],
  ['OTHER', 'bg-red-50 text-red-800'],
])

export const TRACTOR_TYPE_TO_TOOLTIP: Map<string, string> = new Map([
  ['TA-TRACT', i18n.t('tractor.tooltips.TA-TRACT', { ns: 'operational' })],
  ['TA-SLEEP', i18n.t('tractor.tooltips.TA-SLEEP', { ns: 'operational' })],
  ['SA-TRACT', i18n.t('tractor.tooltips.SA-TRACT', { ns: 'operational' })],
  ['UNKNOWN', i18n.t('tractor.tooltips.UNKNOWN', { ns: 'operational' })],
  ['OTHER', i18n.t('tractor.tooltips.OTHER', { ns: 'operational' })],
])

export const UNAVAILABLE_TRACTOR_STATUSES = [TractorStatus.Out, TractorStatus.Vac]

export const SEARCH_LIMIT = 5

export enum TRACKING_SOURCE {
  LOAD_BOARD = 'load board',
  MOBILE_DISPATCH = 'mobile dispatch',
  ALL_LOADS = 'All Loads',
  LOAD_ENTRY = 'Load Entry',
  ACTIVE_LOADS = 'Active Loads',
  SIGN_OFF = 'Sign-Off',
  SIGN_OFF_MODAL = 'Sign-Off Modal',
  DETAIL_VIEW = 'Detail View',
  HOME = 'Home',
  NOTIFICATION_CENTER = 'Notification Center',
  ACTIVE_EXCEPTIONS = 'Active Exceptions',
  EFFICIENCY_MANAGEMENT = 'Efficiency Management',
  DVD = 'DVD',
  LEG = 'leg',
  STOP = 'stop',
}

export enum TRACKING_VIEW_SOURCE {
  MOBILE = 'Mobile',
  DESKTOP = 'Desktop',
}

export const DISPATCHED_DRIVER_REASSIGN_WARNING = i18n.t(
  'dispatch.dispatchedDriverReassign',
  { ns: 'operational' }
)
export const AUTH_TO_DRIVE_WARNING = i18n.t('driver.authToDrive', {
  ns: 'operational',
})
export const SCREEN_SIZE = 1624

export const TRAILER_TYPE_COLOR: Map<TrailerType, string> = new Map([
  [TrailerType.Flatbed, 'bg-green-50 text-green-800'],
  [TrailerType.Tanker, 'bg-blue-50 text-blue-800'],
  [TrailerType.Reefer, 'bg-darkPurple-50 text-darkPurple-800'],
  [TrailerType.Dry, 'bg-yellow-50 text-yellow-800'],
  [TrailerType.Unknown, 'bg-red-50 text-red-800'],
])

export const TRAILER_TYPE: Map<TrailerType, string> = new Map([
  [
    TrailerType.Flatbed,
    i18n.t(`trailer.types.${TrailerType.Flatbed.toLowerCase()}`, { ns: 'operational' }),
  ],
  [
    TrailerType.Tanker,
    i18n.t(`trailer.types.${TrailerType.Tanker.toLowerCase()}`, { ns: 'operational' }),
  ],
  [
    TrailerType.Reefer,
    i18n.t(`trailer.types.${TrailerType.Reefer.toLowerCase()}`, { ns: 'operational' }),
  ],
  [
    TrailerType.Dry,
    i18n.t(`trailer.types.${TrailerType.Dry.toLowerCase()}`, { ns: 'operational' }),
  ],
  [
    TrailerType.Unknown,
    i18n.t(`trailer.types.${TrailerType.Unknown.toLowerCase()}`, { ns: 'operational' }),
  ],
])

export enum STOP_TYPES {
  EQMV = 'EQMV',
  RSC = 'RSC',
}
